import React from 'react';
import { CheckCircle, RotateCcw, Play, History, Clock, AlertTriangle, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { Task } from '../../lib/db';
import TimeEntryModal from './TimeEntryModal';
import TaskHistoryModal from './TaskHistoryModal';
import RegularTaskTemplates from './RegularTaskTemplates';
import { useRegularTaskListLogic } from './useRegularTaskListLogic';

interface RegularTaskListProps {
  templates: Task[];
  todaysTasks: Task[];
  overdueTasks: Task[];
  todaysProgress: { total: number; completed: number };
  lastCompletions: Map<string, Date | null>;
  dbUserId: string;
  onTaskCreate: (task: Partial<Task>) => Promise<void>;
  onTaskUpdate: (id: string, updates: Partial<Task>) => Promise<void>;
  onTaskDelete: (id: string) => Promise<void>;
}

function RegularTaskList({ templates, todaysTasks, overdueTasks, todaysProgress, lastCompletions, dbUserId, onTaskCreate, onTaskUpdate, onTaskDelete }: RegularTaskListProps) {
  const {
    today,
    timeEntryTask,
    setTimeEntryTask,
    showTimeEntry,
    setShowTimeEntry,
    completedTaskIds,
    historyTask,
    setHistoryTask,
    showHistory,
    setShowHistory,
    taskEstimates,
    hasBrokenTasks,
    checkingBrokenTasks,
    showCompletedDropdown,
    setShowCompletedDropdown,
    completedTasksWithTime,
    completeTask,
    resetTask,
    reactivateTask,
    startTimeEntry,
    showTaskHistory,
    getCompletionRate,
    getNextRegenerationDate,
    formatLastCompletion,
    formatDuration,
    handleTimeEntrySaved
  } = useRegularTaskListLogic({
    templates,
    todaysTasks,
    overdueTasks,
    todaysProgress,
    dbUserId,
    onTaskUpdate,
    onTaskDelete
  });

  return (
    <div className="space-y-6">
      {/* Temporary Fix Tasks Link - Only show if there are broken tasks */}
      {!checkingBrokenTasks && hasBrokenTasks && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-yellow-700 mb-2">
                Some regular tasks may have been affected by a recent issue and might not be regenerating correctly.
              </p>
              <Link 
                to="/fix-regular-tasks" 
                className="text-sm font-medium text-yellow-700 hover:text-yellow-800 underline"
              >
                Check and fix affected tasks →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Progress Overview */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Today's Progress</h2>
          <span className="text-xs sm:text-sm text-gray-500">{format(today, 'EEE, MMM d')}</span>
        </div>
        <div className="relative pt-1">
          <div className="flex mb-2 items-center justify-between">
            <div>
              <span className="text-xs font-semibold inline-block uppercase text-indigo-600">
                Completion Rate
              </span>
            </div>
            <div className="text-right">
              <span className="text-xs font-semibold inline-block text-indigo-600">
                {getCompletionRate()}%
              </span>
            </div>
          </div>
          <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-gray-200">
            <div
              style={{ width: `${getCompletionRate()}%` }}
              className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-indigo-600 transition-all duration-300"
            />
          </div>
          <div className="text-center text-sm text-gray-600">
            <div className="flex items-center justify-center gap-2">
              <span>{todaysProgress.completed} of {todaysProgress.total} tasks completed</span>
              {todaysProgress.completed > 0 && (
                <button
                  onClick={() => setShowCompletedDropdown(!showCompletedDropdown)}
                  className="inline-flex items-center p-1 hover:bg-gray-100 rounded transition-colors"
                  title="Show completed tasks"
                >
                  {showCompletedDropdown ? (
                    <ChevronUp className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  )}
                </button>
              )}
            </div>
            {showCompletedDropdown && todaysProgress.completed > 0 && (
              <div className="mt-3 bg-gray-50 rounded-lg p-3 text-left">
                <div className="text-xs font-medium text-gray-700 mb-2">Completed Tasks:</div>
                <div className="space-y-2">
                  {completedTasksWithTime.length > 0 ? (
                    completedTasksWithTime.map((task, index) => (
                      <div key={index} className="flex items-center justify-between text-xs">
                        <div className="flex-1">
                          <span className="font-medium text-gray-800">{task.title}</span>
                          {task.description && (
                            <p className="text-gray-600 mt-0.5">{task.description}</p>
                          )}
                        </div>
                        <span className="text-gray-600 ml-2 whitespace-nowrap">
                          {task.timeSpent > 0 ? formatDuration(task.timeSpent) : 'No time tracked'}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-gray-500 italic">Loading completed tasks...</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Overdue Tasks */}
      {overdueTasks.length > 0 && (
        <div className="bg-red-50 rounded-lg shadow-sm border border-red-200">
          <div className="p-4 border-b border-red-200">
            <h3 className="text-lg font-medium text-red-900">Overdue Tasks</h3>
          </div>
          <div className="divide-y divide-red-200">
            {overdueTasks
              .filter(task => !completedTaskIds.has(task.id))
              .map((task) => {
                const dueDate = task.next_due_date ? new Date(task.next_due_date) : new Date(task.start_date || 0);
                // Ensure dueDate is valid before calculating
                const daysOverdue = !isNaN(dueDate.getTime()) 
                  ? Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24))
                  : 0;
                return (
                <div 
                  key={task.id} 
                  className={`p-4 hover:bg-red-100 transition-all duration-1000 ${
                    completedTaskIds.has(task.id) && task.status === 'Complete' 
                      ? 'opacity-0 transform scale-95' 
                      : 'opacity-100 transform scale-100'
                  }`}>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div 
                      className="flex-1 cursor-pointer"
                      onClick={() => showTaskHistory(task)}
                    >
                      <div className="flex items-start justify-between sm:block">
                        <h4 className={`font-medium text-base sm:text-base ${task.status === 'Complete' ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                          {task.title}
                        </h4>
                        <span className={`sm:hidden ml-2 px-2 py-1 text-xs rounded-full ${
                          task.priority === 'Critical' ? 'bg-red-100 text-red-800' :
                          task.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                          task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {task.priority || 'Low'}
                        </span>
                      </div>
                      {task.description && (
                        <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                      )}
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                        <span className="text-xs text-red-600 font-medium">
                          {daysOverdue === 1 ? '1 day overdue' : `${daysOverdue} days overdue`}
                        </span>
                        {/* Highlight bumped tasks so users know they come back tomorrow. */}
                        {task.notes && task.notes.includes('[Deferred to Tomorrow]') && (
                          <span
                            className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded-full"
                            title="This regular task was bumped and will return to Focus Mode tomorrow"
                          >
                            Bumped · Back Tomorrow
                          </span>
                        )}
                        <span className="text-xs text-gray-500">
                          {task.frequency || 'Daily'}
                        </span>
                        <span className="text-xs text-gray-500">
                          Last: {formatLastCompletion(lastCompletions.get(task.title))}
                        </span>
                        <span className="text-xs text-blue-600">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {(() => {
                            const estimate = taskEstimates.get(`${task.title}|${task.description || ''}`);
                            return estimate && estimate > 0 
                              ? `Avg: ${formatDuration(estimate)}`
                              : 'Avg: --';
                          })()}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-2">
                      <span className={`hidden sm:inline-flex px-2 py-1 text-xs rounded-full ${
                        task.priority === 'Critical' ? 'bg-red-100 text-red-800' :
                        task.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                        task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {task.priority || 'Low'}
                      </span>
                      <button
                        onClick={() => showTaskHistory(task)}
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        title="View history"
                      >
                        <History className="w-4 h-4" />
                      </button>
                      {task.notes && task.notes.includes('[Deferred to Tomorrow]') && (
                        <button
                          onClick={() => reactivateTask(task)}
                          className="p-2 bg-orange-100 text-orange-600 hover:bg-orange-200 rounded transition-all"
                          title="Make Live Again"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      )}
                      {task.status !== 'Complete' && (
                        <button
                          onClick={() => startTimeEntry(task)}
                          className="px-3 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2"
                          title="Start timer to complete task"
                        >
                          <Play className="w-4 h-4" />
                          <span className="text-sm font-medium hidden sm:inline">Start Timer</span>
                          <span className="text-sm font-medium sm:hidden">Timer</span>
                        </button>
                      )}
                      {task.status === 'Complete' && (
                        <button
                          onClick={() => resetTask(task.id)}
                          className="p-2 text-gray-400 hover:text-gray-600"
                          title="Reset task"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )})
            }
          </div>
        </div>
      )}

      {/* Today's Tasks */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Today's Tasks</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {todaysTasks.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No tasks for today yet. They will appear after midnight!
            </div>
          ) : (
            todaysTasks
              .filter(task => !completedTaskIds.has(task.id))
              .map((task) => (
              <div 
                key={task.id} 
                className={`p-4 hover:bg-gray-50 transition-all duration-1000 ${
                  completedTaskIds.has(task.id) && task.status === 'Complete' 
                    ? 'opacity-0 transform scale-95' 
                    : 'opacity-100 transform scale-100'
                }`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div 
                    className="flex-1 cursor-pointer"
                    onClick={() => showTaskHistory(task)}
                  >
                    <div className="flex items-start justify-between sm:block">
                      <h4 className={`font-medium text-base sm:text-base ${task.status === 'Complete' ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                        {task.title}
                      </h4>
                      <span className={`sm:hidden ml-2 px-2 py-1 text-xs rounded-full ${
                        task.priority === 'Critical' ? 'bg-red-100 text-red-800' :
                        task.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                        task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {task.priority || 'Low'}
                      </span>
                    </div>
                    {task.description && (
                      <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                    )}
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                      <span className="text-xs text-blue-600 font-medium">
                        Due today
                      </span>
                      {task.notes && task.notes.includes('[Deferred to Tomorrow]') && (
                        <span
                          className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded-full"
                          title="Bumped today; it will drop back into Focus Mode tomorrow"
                        >
                          Bumped · Back Tomorrow
                        </span>
                      )}
                      <span className="text-xs text-gray-500">
                        {task.frequency || 'Daily'}
                      </span>
                      <span className="text-xs text-gray-500">
                        Last: {formatLastCompletion(lastCompletions.get(task.title))}
                      </span>
                      {getNextRegenerationDate(task) && (
                        <span className="text-xs text-gray-500">
                          Next: {format(getNextRegenerationDate(task)!, 'MMM d')}
                        </span>
                      )}
                      <span className="text-xs text-blue-600">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {(() => {
                          const estimate = taskEstimates.get(`${task.title}|${task.description || ''}`);
                          return estimate && estimate > 0 
                            ? `Avg: ${formatDuration(estimate)}`
                            : 'Avg: --';
                        })()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-2">
                    <span className={`hidden sm:inline-flex px-2 py-1 text-xs rounded-full ${
                      task.priority === 'Critical' ? 'bg-red-100 text-red-800' :
                      task.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                      task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {task.priority || 'Low'}
                    </span>
                    <button
                      onClick={() => showTaskHistory(task)}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      title="View history"
                    >
                      <History className="w-4 h-4" />
                    </button>
                    {task.notes && task.notes.includes('[Deferred to Tomorrow]') && (
                      <button
                        onClick={() => reactivateTask(task)}
                        className="p-2 bg-orange-100 text-orange-600 hover:bg-orange-200 rounded transition-all"
                        title="Make Live Again"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    )}
                    {task.status !== 'Complete' && (
                      <button
                        onClick={() => startTimeEntry(task)}
                        className="px-3 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2"
                        title="Start timer to complete task"
                      >
                        <Play className="w-4 h-4" />
                        <span className="text-sm font-medium hidden sm:inline">Start Timer</span>
                        <span className="text-sm font-medium sm:hidden">Timer</span>
                      </button>
                    )}
                    {task.status === 'Complete' && (
                      <button
                        onClick={() => resetTask(task.id)}
                        className="p-2 text-gray-400 hover:text-gray-600"
                        title="Reset task"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Existing Regular Tasks */}
      <RegularTaskTemplates
        templates={templates}
        lastCompletions={lastCompletions}
        onTaskCreate={onTaskCreate}
        onTaskUpdate={onTaskUpdate}
        onTaskDelete={onTaskDelete}
        dbUserId={dbUserId}
        onTaskClick={(task) => {
          setHistoryTask({ 
            templateId: task.id, 
            title: task.title,
            description: task.description,
            frequency: task.frequency,
            priority: task.priority
          });
          setShowHistory(true);
        }}
      />

      {/* Time Entry Modal */}
      {showTimeEntry && timeEntryTask && (
        <TimeEntryModal
          task={timeEntryTask}
          dbUserId={dbUserId}
      onClose={() => {
        setShowTimeEntry(false);
        setTimeEntryTask(null);
      }}
      onTimeEntrySaved={async (wasCompleted) => {
        await handleTimeEntrySaved(wasCompleted);
      }}
        />
      )}

      {/* Task History Modal */}
      {showHistory && historyTask && (
        <TaskHistoryModal
          taskTemplateId={historyTask.templateId}
          taskTitle={historyTask.title}
          taskDescription={historyTask.description}
          taskFrequency={historyTask.frequency}
          taskPriority={historyTask.priority}
          dbUserId={dbUserId}
          onClose={() => {
            setShowHistory(false);
            setHistoryTask(null);
          }}
        />
      )}
    </div>
  );
}

export default RegularTaskList;
